import {BaseApi} from './base-api';
import Vue from 'vue';

export class SettlementApi extends BaseApi {
  static apiUri = 'settlements';
  static bookingsSubUri = 'bookings';
  static objectsSubUri = 'objects';

  /** @return {Promise<EntityHttpResponse<SettlementDto>>} */
  static getById (id) {
    return super.getById(id);
  }

  /**
   * @param {number} year
   * @param {number} month
   * @return {Promise<EntityHttpResponse<SettlementDto>>}
   */
  static createSettlement(year, month) {
    return Vue.http.post(this.apiUri + '/', {year: year, month: month});
  }

  /**
   * @return {Promise<EntityHttpResponse<BookingValidationResult>>}
   * @param {number} settlementId
   */
  static validateSettlementBookings(settlementId) {
    return Vue.http.get([this.apiUri, settlementId, this.bookingsSubUri, 'validate'].join('/'), {timeout: 60 * 1000});
  }

  /**
   * @return {Promise<EntityHttpResponse<SettlementRenewResultDto>>}
   * @param {number} settlementId
   * @param {SettlementBookingsRequest} settlementBookingsRequest
   */
  static updateSettlementBookings(settlementId, settlementBookingsRequest) {
    return Vue.http.patch([this.apiUri, settlementId, this.bookingsSubUri].join('/'), settlementBookingsRequest, {timeout: 5 * 60 * 1000});
  }

  /**
   * @return {Promise<EntityHttpResponse<PageResponse<ObjectBasicDto>>>}
   * @param {number} settlementId
   * @param {HttpOptions} params
   */
  static getMissingSettlementObjects(settlementId, params) {
    return Vue.http.get(this.apiUri + '/' + settlementId + '/missing-objects', params);
  }

  /**
   * @return {Promise<EntityHttpResponse<PageResponse<ObjectBasicDto>>>}
   * @param {number} settlementId
   */
  static createMissingSettlementObjects(settlementId) {
    return Vue.http.post(this.apiUri + '/' + settlementId + '/missing-objects/create');
  }

  /**
   * @return {Promise<EntityHttpResponse<SettlementObjectsReadyToBeSentToOwnersDto>>}
   * @param {number} settlementId
   * @param {number[]} objectIds
   */
  static getSettlementObjectsReadyToBeSentToOwners(settlementId, objectIds = null) {
    return Vue.http.post(this.apiUri + '/' + settlementId + '/sendable-to-owners', objectIds);
  }

  /**
   * @return {Promise<HttpResponse>}
   * @param {number} settlementId
   * @param {SettlementSendToOwnersRequest} settlementSendToOwnersRequest
   */
  static sendToOwners(settlementId, settlementSendToOwnersRequest) {
    return Vue.http.post(this.apiUri + '/' + settlementId + '/send-to-owners', settlementSendToOwnersRequest);
  }

  /**
   * @return {Promise<HttpResponse>}
   * @param {number} settlementId
   * @param {number} settlementObjectId
   * @param {boolean} checked
   */
  static setSettlementObjectChecked(settlementId, settlementObjectId, checked) {
    return Vue.http.post(this.apiUri + '/' + settlementId + '/' + this.objectsSubUri + '/' + settlementObjectId + '/set-checked', {checked: checked});
  }

  /**
   * @return {Promise<HttpResponse>}
   * @param {number} settlementId
   * @param {number} settlementObjectId
   * @param {boolean} sent
   */
  static setSettlementObjectSentToOwner(settlementId, settlementObjectId, sent) {
    return Vue.http.post(this.apiUri + '/' + settlementId + '/' + this.objectsSubUri + '/' + settlementObjectId + '/set-sent-to-owner', {sent: sent});
  }

  /**
   * @return {Promise<HttpResponse>}
   * @param {number} settlementId
   * @param {number} settlementObjectId
   * @param {boolean} sent
   */
  static renewSettlementObject(settlementId, settlementObjectId) {
    return Vue.http.post(this.apiUri + '/' + settlementId + '/' + this.objectsSubUri + '/' + settlementObjectId + '/renew');
  }

  /**
   * @return {Promise<EntityHttpResponse<OpenCommissionDto>>}
   */
  static getLatestOpenCommissionTotal() {
    return Vue.http.get(this.apiUri + '/open-commission-total/latest');
  }

  // ----- File Downloads -----

  /**
   * @return {Promise<HttpResponse<ArrayBuffer>>}
   * @param {number} settlementId
   */
  static downloadSettlementObjectPdfPackage(settlementId) {
    return Vue.http.get(this.apiUri + '/' + settlementId + '/pdf', {responseType: 'arraybuffer'});
  }

  /**
   * @return {Promise<HttpResponse<ArrayBuffer>>}
   * @param {number} settlementId
   */
  static downloadSettlementDatevCsvPackage(settlementId) {
    return Vue.http.get([this.apiUri, settlementId, 'datev'].join('/'), {responseType: 'arraybuffer'});
  }

  /**
   * @return {Promise<HttpResponse<ArrayBuffer>>}
   * @param {number} settlementId
   * @param {string} type
   */
  static downloadSettlementSepa(settlementId, type) {
    return Vue.http.get([this.apiUri, settlementId, 'sepa', type].join('/'), {responseType: 'arraybuffer'});
  }

  /**
   * @return {Promise<HttpResponse<ArrayBuffer>>}
   * @param {number} settlementId
   * @param {number} settlementObjectId
   */
  static downloadSettlementObjectPdf(settlementId, settlementObjectId) {
    return Vue.http.get([this.apiUri, settlementId, this.objectsSubUri, settlementObjectId, 'pdf'].join('/'), {responseType: 'arraybuffer'});
  }

  /**
   * @return {Promise<HttpResponse<ArrayBuffer>>}
   * @param {number} settlementId
   * @param {number} settlementObjectId
   */
  static downloadSettlementObjectDatevCsv(settlementId, settlementObjectId) {
    return Vue.http.get([this.apiUri, settlementId, this.objectsSubUri, settlementObjectId, 'datev'].join('/'), {responseType: 'arraybuffer'});
  }

  /**
   * @return {Promise<HttpResponse<ArrayBuffer>>}
   * @param {number} settlementId
   * @param {number} settlementObjectId
   */
  static downloadSettlementObjectSepa(settlementId, settlementObjectId) {
    return Vue.http.get([this.apiUri, settlementId, this.objectsSubUri, settlementObjectId, 'sepa'].join('/'), {responseType: 'arraybuffer'});
  }

  /**
   * @return {Promise<HttpResponse<ArrayBuffer>>}
   * @param {number} year
   * @param {number} month
   */
  static downloadOpenCommissionCsv(year, month) {
    return Vue.http.get(this.apiUri + '/open-commissions/csv', {responseType: 'arraybuffer', params: {year: year, month: month}});
  }

  /** @return {Promise<EntityHttpResponse<SettlementDto>>} */
  static getSettlementPreview (year, month) {
    return Vue.http.get(this.apiUri + '/preview', {params: {year: year, month: month}, timeout: 60 * 1000});
  }

  /** @return {Promise<EntityHttpResponse<SettlementDto>>} */
  static getSettlementPreviewPdf (year, month, objectId) {
    return Vue.http.get([this.apiUri, 'preview', year, month, objectId, 'pdf'].join('/'), {responseType: 'arraybuffer'});
  }
}
